import React, { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown, Search, X, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface IconOption {
  icon: string;
  link?: string;
}

interface IconSelectProps {
  options: IconOption[];
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const IconSelect: React.FC<IconSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => (o.link || o.icon) === value);

  const filtered = options.filter((o) =>
    o.icon.toLowerCase().includes(search.toLowerCase())
  );

  // Focus search input when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearch("");
      setHighlighted(null);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectOption = useCallback((val: string) => {
    onChange(val);
    setOpen(false);
    setSearch("");
    setHighlighted(null);
  }, [onChange]);

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || disabled) return;
    const idx = filtered.findIndex((o) => (o.link || o.icon) === highlighted);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = idx < filtered.length - 1 ? idx + 1 : 0;
      setHighlighted(filtered[next] ? (filtered[next].link || filtered[next].icon) : null);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = idx > 0 ? idx - 1 : filtered.length - 1;
      setHighlighted(filtered[prev] ? (filtered[prev].link || filtered[prev].icon) : null);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlighted) selectOption(highlighted);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex flex-row items-center w-full h-[40px] rounded-[8px] border border-solid bg-white px-4 py-0 text-base font-normal text-[#242424] focus:outline-none focus:ring-0 transition-colors md:text-sm",
          open ? "border-[#A09D9B]" : "border-[#CCC8C6]",
          disabled && "opacity-50 cursor-not-allowed bg-[#F5F5F5]"
        )}
      >
        {/* Selected preview */}
        <span className={cn("truncate text-left flex-1 flex items-center gap-2", !selected && "text-[#757271]")}>
          {selected ? (
            <>
              {selected.link && (
                <img src={selected.link} alt={selected.icon} className="w-5 h-5 flex-shrink-0" />
              )}
              <span className="truncate">{selected.icon}</span>
            </>
          ) : (
            placeholder
          )}
        </span>

        {/* Right icons */}
        <div className="flex items-center gap-2 ml-2">
          {value && !disabled && (
            <X
              className="h-4 w-4 text-gray-500 hover:text-gray-800 cursor-pointer"
              onClick={clearSelection}
            />
          )}
          <ChevronDown className="h-[18px] w-[18px] text-[#757271]" />
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-2 w-full rounded-[12px] border border-[#E2E8F0] bg-white shadow-[0_0_0_1px_rgba(46,43,41,0.1),0_8px_16px_-4px_rgba(46,43,41,0.03),0_8px_16px_-4px_rgba(46,43,41,0.1)] flex flex-col max-h-[314px]">
          {/* Search bar — matches Lift UI exactly */}
          <div className="flex items-center w-full px-3 pt-3 pb-2">
            <div className="flex items-center w-full h-[34px] bg-white border border-[#E5E1DF] rounded-[8px] px-2">
              <Search className="h-[18px] w-[18px] text-[#757271] mr-2 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setHighlighted(filtered[0] ? (filtered[0].link || filtered[0].icon) : null);
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="w-full bg-transparent outline-none border-none text-base font-normal text-[#757271] placeholder:text-[#757271] md:text-sm"
                style={{ fontFamily: "Figtree", height: "34px" }}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="px-1 pb-2 overflow-auto" style={{ maxHeight: 240 }}>
            {filtered.length === 0 ? (
              <div className="text-center text-[#757271] py-4 text-base md:text-sm">
                {search ? "No icons found" : "No options"}
              </div>
            ) : (
              filtered.map((opt) => {
                const val = opt.link || opt.icon;
                const isSelected = val === value;
                const isHighlighted = val === highlighted;
                return (
                  <button
                    key={val}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectOption(val);
                    }}
                    onMouseEnter={() => setHighlighted(val)}
                    className={cn(
                      "flex flex-row justify-between items-center w-full cursor-pointer select-none rounded-[8px] px-4 py-2 text-base font-medium leading-[18px] text-[#585755] outline-none transition-colors md:text-sm h-[40px]",
                      isSelected && "bg-[#F4FFC6] text-[#242424]",
                      isHighlighted && !isSelected && "bg-[#F8FAFC]",
                      !isSelected && !isHighlighted && "hover:bg-[#F8FAFC]"
                    )}
                  >
                    <span className="flex items-center gap-2 truncate text-left flex-1">
                      {opt.link && (
                        <img src={opt.link} alt={opt.icon} className="w-5 h-5 flex-shrink-0" />
                      )}
                      <span className="truncate">{opt.icon}</span>
                    </span>
                    {isSelected && (
                      <Check className="h-[18px] w-[18px] text-[#585755] ml-2 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IconSelect;
